if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.skuFallbackCache = new Map();
        this.focusElement = this.input;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
        this.addEventListener('submit', this.onSubmit.bind(this));
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.setSubmitMode(false);
        this.input.focus();
        this.removeAttribute('results');
      }

      getQuery() {
        return this.input.value.trim();
      }

      get searchableFields() {
        return 'title,product_type,variants.title,vendor,variants.sku';
      }

      get prefixInput() {
        return this.querySelector('input[name="options[prefix]"]');
      }

      get typeInput() {
        return this.querySelector('input[name="type"]');
      }

      getOrCreateTypeInput() {
        if (this.typeInput) return this.typeInput;

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'type';
        this.appendChild(input);
        return input;
      }

      isSkuLikeQuery(query) {
        return query.length >= 3 && !query.includes(' ') && /[\d-]/.test(query);
      }

      setSubmitMode(isSkuSearch) {
        if (this.prefixInput) {
          this.prefixInput.value = isSkuSearch ? 'none' : 'last';
        }

        if (isSkuSearch) {
          this.getOrCreateTypeInput().value = 'product';
        } else if (this.typeInput) {
          this.typeInput.remove();
        }
      }

      buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][fields]', this.searchableFields);
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      buildJsonUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}.json`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', 10);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][fields]', this.searchableFields);
        return url;
      }

      buildSearchFallbackUrl() {
        const isSkuSearch = this.isSkuLikeQuery(this.getQuery());
        const url = new URL(`${theme.routes.shop_url}${theme.routes.search_url}`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', isSkuSearch ? 'none' : 'last');
        url.searchParams.set('section_id', 'predictive-search-sku-results');
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.setSubmitMode(false);
          this.clear();
          return;
        }

        this.hideTypewriter();
        this.setSubmitMode(this.isSkuLikeQuery(this.getQuery()));

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      async onSubmit(event) {
        const searchTerm = this.getQuery();
        const isSkuSearch = this.isSkuLikeQuery(searchTerm);

        this.setSubmitMode(isSkuSearch);

        if (!isSkuSearch) return;

        event.preventDefault();

        this.submitAbortController?.abort();
        this.submitAbortController = new AbortController();

        this.setAttribute('loading', '');

        try {
          const response = await fetch(this.buildJsonUrl().toString(), { signal: this.submitAbortController.signal });
          if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

          const data = await response.json();
          const products = data?.resources?.results?.products || [];

          if (products.length === 1 && products[0]?.url) {
            window.location.href = products[0].url;
            return;
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error(error);
          }
        } finally {
          this.removeAttribute('loading');
        }

        HTMLFormElement.prototype.submit.call(this);
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
            this.removeAttribute('loading');
            this.removeAttribute('results');
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return;

        const parsed = new DOMParser().parseFromString(responseText, 'text/html');
        const parsedResults = parsed.getElementById(id);
        if (!parsedResults) {
          this.removeAttribute('loading');
          this.removeAttribute('results');
          return;
        }
        document.getElementById(id).innerHTML = parsedResults.innerHTML;

        this.filterCollectionResults(id);
        void this.renderSkuSearchFallback(id);
      }

      filterCollectionResults(resultsId) {
        const collectionHandle = this.dataset.collectionHandle;
        if (!collectionHandle || collectionHandle === 'all') return null;

        const resultsEl = document.getElementById(resultsId);
        if (!resultsEl) return null;

        const productsContainer = resultsEl.querySelector('.horizontal-products');
        if (!productsContainer) return null;

        let visibleCount = 0;
        productsContainer.querySelectorAll('.horizontal-product').forEach((card) => {
          const handles = (card.dataset.collectionHandles || '')
            .split(',')
            .map((handle) => handle.trim())
            .filter(Boolean);

          const matches = !handles.length || handles.includes(collectionHandle);
          if (matches) {
            card.removeAttribute('hidden');
            card.style.removeProperty('display');
            visibleCount += 1;
          } else {
            card.setAttribute('hidden', 'hidden');
            card.style.display = 'none';
          }
        });

        const section = productsContainer.closest('.grid');
        if (!section) return;

        if (visibleCount === 0) {
          section.setAttribute('hidden', 'hidden');
          section.style.display = 'none';
        } else {
          section.removeAttribute('hidden');
          section.style.removeProperty('display');
        }

        return visibleCount;
      }

      hasVisibleProductResults(resultsId) {
        const resultsEl = document.getElementById(resultsId);
        if (!resultsEl) return false;

        const cards = resultsEl.querySelectorAll('.horizontal-products .horizontal-product');
        if (cards.length === 0) return false;

        return Array.from(cards).some((card) => !card.hasAttribute('hidden') && card.style.display !== 'none');
      }

      toggleSkuFallbackEmptyState(resultsId) {
        const resultsEl = document.getElementById(resultsId);
        if (!resultsEl) return;

        const productsSection = resultsEl.querySelector('[data-sku-products-section]');
        const emptyState = resultsEl.querySelector('[data-sku-empty-state]');
        const submitButton = resultsEl.querySelector('[data-sku-submit-button]');
        if (!emptyState) return;

        const hasVisibleProducts = this.hasVisibleProductResults(resultsId);
        if (hasVisibleProducts) {
          emptyState.setAttribute('hidden', 'hidden');
          productsSection?.removeAttribute('hidden');
          productsSection?.style.removeProperty('display');
          submitButton?.removeAttribute('hidden');
          submitButton?.style.removeProperty('display');
        } else {
          emptyState.removeAttribute('hidden');
          productsSection?.setAttribute('hidden', 'hidden');
          productsSection?.style.setProperty('display', 'none');
          submitButton?.setAttribute('hidden', 'hidden');
          submitButton?.style.setProperty('display', 'none');
        }
      }

      async renderSkuSearchFallback(resultsId) {
        const query = this.getQuery();
        if (!query.length) return;
        if (this.hasVisibleProductResults(resultsId)) return;

        const url = this.buildSearchFallbackUrl().toString();
        const applyFallback = (responseText) => {
          if (query !== this.getQuery()) return;

          const resultsEl = document.getElementById(resultsId);
          if (!resultsEl) return;

          const parsed = new DOMParser().parseFromString(responseText, 'text/html');
          const parsedResults = parsed.querySelector('[id^="SkuPredictiveSearchResults-"]');
          if (!parsedResults) return;

          resultsEl.innerHTML = parsedResults.innerHTML;
          this.filterCollectionResults(resultsId);
          this.toggleSkuFallbackEmptyState(resultsId);
        };

        if (this.skuFallbackCache.has(url)) {
          applyFallback(this.skuFallbackCache.get(url));
          return;
        }

        this.skuFallbackAbortController?.abort();
        this.skuFallbackAbortController = new AbortController();

        try {
          const response = await fetch(url, { signal: this.skuFallbackAbortController.signal });
          if (!response.ok) throw new Error(`SKU search fallback failed: ${response.status}`);

          const responseText = await response.text();
          this.skuFallbackCache.set(url, responseText);
          applyFallback(responseText);
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error(error);
          }
        }
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
